import Link from 'next/link';
import React, { SyntheticEvent, useCallback } from 'react';

import styled from '@emotion/styled';
import { buttonClasses } from '@mui/material';
import { useTranslations } from 'next-intl';
import { useCopyToClipboard } from 'usehooks-ts';

import { useCurrentCompletedUser } from '@web/features/user/service';
import { Avatar, Button, ShareMenu, showSnackbar } from '@web/ui/components';
import { GearIcon, ShareIcon } from '@web/ui/icons';
import { getBorder } from '@web/ui/theme/utils';

import { HubTile } from '../HubTile';

import { UserHubTileNotConnected } from './UserHubTileNotConnected';

const UserTileContainer = styled(HubTile)`
  position: relative;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.color.pink};
  padding: ${({ theme }) => theme.spacing('0500')};
  gap: ${({ theme }) => theme.spacing('0500')};
  height: auto;

  :active {
    img {
      border-radius: ${({ theme }) => theme.shape.corner['2000']};
    }
  }
`;

const User = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing('0750')};
`;

const UserName = styled.span`
  text-overflow: ellipsis;
  overflow: hidden;
  max-width: 15.125rem;
  white-space: nowrap;
  padding-block: ${({ theme }) => theme.spacing('0250')};
  margin-block: -${({ theme }) => theme.spacing('0250')};
  color: ${({ theme }) => theme.color.onTealContainer};
  font: ${({ theme }) => theme.typography.body.bold};
  font-size: ${({ theme }) => theme.typescale.size['2']};
  line-height: ${({ theme }) => theme.typescale.lineHeight.condensed};
`;

const TwitterUsername = styled.span`
  color: ${({ theme }) => theme.color.onTealContainer};
  font: ${({ theme }) => theme.typography.mono.regular};
  font-size: ${({ theme }) => theme.typescale.size['0875']};
  line-height: ${({ theme }) => theme.typescale.lineHeight.condensed};
  cursor: pointer;

  :hover {
    text-decoration: ${({ theme }) => theme.textDecoration.underline};
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing('1500')};
`;

const StyledAvatar = styled(Avatar)`
  aspect-ratio: 1;
  width: 100%;
  height: auto;
  flex: 1;
  z-index: 1;
  box-sizing: border-box;

  img {
    box-sizing: border-box;
    width: 100%;
    height: 100%;
    object-fit: cover;
    ${getBorder('1', 'dark', '1500')};
  }
`;

const Actions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing('0250')};
`;

const ShortButton = styled(Button)`
  width: ${({ theme }) => theme.spacing('3000')};

  ${({ theme }) => theme.breakpoints.up('sm')} {
    &.${buttonClasses.root} {
      width: ${({ theme }) => theme.spacing('3000')};
    }
  }
`;

const StyledLink = styled(Link)`
  width: 100%;
`;

const ShareMenuContainer = styled.div``;

export const UserHubTile = () => {
  const [, copy] = useCopyToClipboard();
  const t = useTranslations();
  const completedUser = useCurrentCompletedUser();

  const handleCopyWalletAddress = useCallback(
    (e: SyntheticEvent<HTMLSpanElement>) => {
      e.preventDefault();

      if (!completedUser) {
        return;
      }

      showSnackbar(t('WALLET_COPY_SUCCESS'));
      copy(completedUser.user.displayWallet);
    },
    [completedUser?.user.displayWallet],
  );

  return completedUser ? (
    <Link href="/profile">
      <UserTileContainer stateLayer="onPink" background="pink">
        <StyledAvatar
          src={completedUser.user.twitter.profilePictureURL}
          alt={`${completedUser.user.twitter.displayName}'s profile picture`}
          borderRadius="1500"
          tokenId={completedUser.user.twitter.personaPFPTokenId}
        />
        <UserInfo>
          <User>
            <UserName>{completedUser.user.twitter.displayName}</UserName>
            <TwitterUsername onClick={handleCopyWalletAddress}>
              @{completedUser.user.twitter.handle}
            </TwitterUsername>
          </User>
          <Actions>
            <StyledLink href="/settings">
              <ShortButton
                label={<GearIcon />}
                variant="secondary"
                background="tealContainer"
                color="onTealContainer"
              />
            </StyledLink>
            <ShareMenuContainer onClick={(e) => e.preventDefault()}>
              <ShareMenu
                title={t('USER_HUB_TILE_SHARE_MENU_TITLE')}
                link={`${window.location.origin}/profile?u=${completedUser.user.twitter.handle}`}
                Trigger={
                  <ShortButton
                    label={<ShareIcon />}
                    variant="secondary"
                    background="tealContainer"
                    color="onTealContainer"
                  />
                }
              />
            </ShareMenuContainer>
          </Actions>
        </UserInfo>
      </UserTileContainer>
    </Link>
  ) : (
    <UserHubTileNotConnected />
  );
};
